import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Crown, TrendingUp, ArrowRight, Sparkles, Shield } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface PersonaSelectorProps {
  onSelectInvestor: () => void;
}

export const PersonaSelector = ({ onSelectInvestor }: PersonaSelectorProps) => {
  const navigate = useNavigate();
  const [rememberChoice, setRememberChoice] = useState(true);

  const handleSelect = (persona: "ceo" | "investor") => {
    if (rememberChoice) {
      localStorage.setItem(
        "provenance-persona",
        JSON.stringify({
          persona,
          timestamp: Date.now(),
          rememberChoice: true,
        }),
      );
    }

    if (persona === "ceo") {
      navigate("/for-founders");
    } else {
      onSelectInvestor();
    }
  };

  const personas = [
    {
      id: "ceo" as const,
      icon: Crown,
      title: "I'm a tech founder or CEO",
      description: "I want ongoing visibility into my company's technology health",
      features: ["Monthly health check reports", "Plain-language insights", "Questions to ask your CTO"],
      gradient: "from-amber-500/20 to-orange-500/20",
      iconColor: "text-amber-500",
      borderColor: "border-amber-500/30 hover:border-amber-500/60",
    },
    {
      id: "investor" as const,
      icon: TrendingUp,
      title: "I work in M&A or investment",
      description: "I need technology due diligence for acquisitions",
      features: ["Adversarial code analysis", "Deal-breaking risk identification", "IC-ready reports"],
      gradient: "from-primary/20 to-blue-500/20",
      iconColor: "text-primary",
      borderColor: "border-primary/30 hover:border-primary/60",
    },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex flex-col bg-background"
      >
        {/* Minimal header with just logo */}
        <div className="h-16 border-b border-border/50 bg-background/80 backdrop-blur-xl">
          <div className="container mx-auto flex h-full items-center px-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl font-bold tracking-tight">PROVENANCE</span>
            </Link>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center overflow-auto py-8">
          <div className="container max-w-5xl px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 mb-6">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Welcome to PROVENANCE</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-4">
                How can we help you today?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Choose your path to get started with the experience that's right for you
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {personas.map((persona, index) => (
                <motion.button
                  key={persona.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  onClick={() => handleSelect(persona.id)}
                  className={`group relative p-8 rounded-2xl border-2 ${persona.borderColor} bg-gradient-to-br ${persona.gradient} text-left transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl`}
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative">
                    <div className={`inline-flex p-3 rounded-xl bg-background/50 ${persona.iconColor} mb-6`}>
                      <persona.icon className="h-8 w-8" />
                    </div>

                    <h2 className="text-2xl font-bold mb-2">{persona.title}</h2>
                    <p className="text-muted-foreground mb-6">{persona.description}</p>

                    <ul className="space-y-2 mb-8">
                      {persona.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <div className={`h-1.5 w-1.5 rounded-full ${persona.iconColor.replace("text-", "bg-")}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all">
                      Get started
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
            >
              <Checkbox
                id="remember"
                checked={rememberChoice}
                onCheckedChange={(checked) => setRememberChoice(checked as boolean)}
              />
              <label htmlFor="remember" className="cursor-pointer">
                Remember my choice for next time
              </label>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
